import React, { useState } from "react";
import { useUserApi } from "../../../../services/UserApi";
import UserLogsTable from "./UserLogsTable";
import UtcDateRange from "../../../common/UtcDateRange";
import ErrorMessage from "../../../messages/ErrorMessage";
import SearchButton from "../../../common/SearchButton";

import "./UserLogs.css";

const buildInitialDateRange = () => {
  const now = new Date();
  return {
    dateFrom: new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).toISOString(),
    dateTo: new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    ).toISOString(),
  };
};

const UserLogs = (props) => {
  const pagingDefaults = {
    skip: 0,
    pageSize: 15,
  };
  const initialDateRange = buildInitialDateRange();
  const [fromDateUtc, setFromDateUtc] = useState(initialDateRange.dateFrom);
  const [toDateUtc, setToDateUtc] = useState(initialDateRange.dateTo);
  const [cachedLogsPageSize, setCachedLogsPageSize] = useState(
    pagingDefaults.pageSize
  );
  const [logsResultSet, setLogsResultSet] = useState({
    logs: [],
    pageSize: cachedLogsPageSize,
    currentPage: 0,
    lastPage: true,
  });
  const [loading, setLoading] = useState(false);
  const { fetchUserLogs } = useUserApi();
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorBannerMsg, setErrorBannerMsg] = useState("");

  const fetchLogs = async (
    _fromDateUtc = fromDateUtc,
    _toDateUtc = toDateUtc,
    skip = pagingDefaults.skip,
    pageSize = cachedLogsPageSize
  ) => {
    try {
      setLoading(true);
      setErrorBannerMsg("");
      setShowErrorBanner(false);

      const apiResult = await fetchUserLogs(
        props.user.userId,
        new Date(_fromDateUtc),
        new Date(_toDateUtc),
        skip,
        pageSize
      );

      if (!apiResult?.success || !apiResult?.data)
        throw new Error(apiResult?.error?.description || "Unexpected response");

      const resultSet = {
        logs: apiResult.data?.logs || [],
        currentPage: skip / pageSize + 1,
        pageSize: pageSize,
        lastPage: apiResult?.data.logs.length < pageSize,
      };

      setLogsResultSet(resultSet);
    } catch (error) {
      setErrorBannerMsg(error?.toString() || "Failed to retrieve log data");
      setShowErrorBanner(true);
    }
    setLoading(false);
  };

  const onSearchDateRangeUpdate = async (newFromDateUtc, newToDateUtc) => {
    setFromDateUtc(newFromDateUtc);
    setToDateUtc(newToDateUtc);
    fetchLogs(newFromDateUtc, newToDateUtc);
  };

  const onChangePage = async (pageId) => {
    if (pageId.pageSize !== cachedLogsPageSize)
      setCachedLogsPageSize(pageId.pageSize);

    await fetchLogs(fromDateUtc, toDateUtc, pageId.skip);
  };

  const searchFilter = (
    <form
      className="userLogsFilters"
      onSubmit={(e) => {
        e.preventDefault();
        fetchLogs();
      }}
    >
      <div className="user-logs-search-filter-body">
        <UtcDateRange
          idPrefix="user-logs-filter-date-range"
          fromDateUtc={fromDateUtc}
          toDateUtc={toDateUtc}
          onChange={onSearchDateRangeUpdate}
          fromLabel="From"
          toLabel="To"
          inlineLabels={true}
          className="user-logs-filter-date-range"
        />
        <SearchButton id="user-logs-search-btn" type="submit" />
      </div>
    </form>
  );

  if (props.user === null) return null;

  return (
    <>
      {searchFilter}
      {showErrorBanner ? (
        <ErrorMessage message={errorBannerMsg} />
      ) : (
        <UserLogsTable
          logsResultSet={logsResultSet}
          loading={loading}
          onChangePage={onChangePage}
        />
      )}
    </>
  );
};

export default UserLogs;
